import { useState } from 'react';
import { api, clock, humanise, km, today } from '../api';
import { PageHead, Panel, ShortId, State, Table, tripTone, useLoad } from '../ui';
import { TripMap } from '../TripMap';

export function Trips() {
  const [date, setDate] = useState(today());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const { data, error, loading, reload } = useLoad(() => api.trips(date), {
    deps: [date],
    // Only poll today — scrolling back through history should not refetch.
    pollMs: date === today() ? 20_000 : undefined,
  });

  const plan = async (direction: 'morning' | 'evening') => {
    if (!window.confirm(
      `Run matching for ${date} ${direction}?\n\n` +
      'This makes metered routing calls — roughly 12 per rider. ' +
      'It is safe to re-run: existing pairings are kept where they still fit.',
    )) return;
    setBusy(true);
    setMsg(null);
    try {
      await api.runPlan({ serviceDate: date, direction });
      setMsg(`Matching finished for ${date} ${direction}.`);
      await reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Matching failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        title="Trips"
        blurb="One row per driver run. Legs are the riders on board; a trip is finished when every leg is dropped or skipped. Open a trip to see the planned route against the path actually driven."
      />

      {open ? <TripMap tripId={open} onClose={() => setOpen(null)} /> : null}
      <Panel
        title={`${data?.length ?? 0} on ${date}`}
        right={
          <>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <button className="btn sm" disabled={busy} onClick={() => void plan('morning')}>
              Match morning
            </button>
            <button className="btn sm" disabled={busy} onClick={() => void plan('evening')}>
              Match evening
            </button>
            <button className="btn sm" onClick={() => void reload()} disabled={loading}>
              Refresh
            </button>
          </>
        }
      >
        {msg ? <div className="empty" style={{ paddingBottom: 0 }}>{msg}</div> : null}
        {error ? <div className="empty">{error}</div> : (
          <Table
            rows={data}
            keyOf={(t) => t.id}
            empty={`No trips on ${date}. Matching runs the night before.`}
            columns={[
              {
                head: 'Status',
                shrink: true,
                cell: (t) => <State tone={tripTone(t.status)}>{humanise(t.status)}</State>,
              },
              { head: 'Dir', shrink: true, cell: (t) => t.direction },
              {
                head: 'Driver',
                cell: (t) => (
                  <>
                    <div>{t.driver_name ?? 'Unknown'}</div>
                    <div className="mono" style={{ color: 'var(--ink-faint)' }}>
                      {t.registration_no ?? '—'}
                    </div>
                  </>
                ),
              },
              {
                head: 'Phone',
                shrink: true,
                cell: (t) =>
                  t.driver_phone
                    ? <a href={`tel:${t.driver_phone}`} className="mono">{t.driver_phone}</a>
                    : '—',
              },
              {
                head: 'Riders',
                num: true,
                shrink: true,
                cell: (t) => `${t.dropped} / ${t.legs}`,
              },
              { head: 'Planned', num: true, shrink: true, cell: (t) => km(t.planned_distance_m) },
              { head: 'Started', num: true, shrink: true, cell: (t) => clock(t.started_at) },
              { head: 'Finished', num: true, shrink: true, cell: (t) => clock(t.completed_at) },
              { head: 'ID', shrink: true, cell: (t) => <ShortId id={t.id} /> },
              {
                head: '',
                shrink: true,
                cell: (t) => (
                  <button className="link" onClick={() => setOpen(t.id)}>
                    Route
                  </button>
                ),
              },
            ]}
          />
        )}
      </Panel>
    </>
  );
}
